import { UserAction } from './userActionEnum.js';

export const AGGREGATION_CUMULATIVE = 'cumulative';
export const AGGREGATION_INDIVIDUAL = 'individual';

export const DOCUMENT_PROCESSING_METRIC_LABELS = {
  timeToOpen: 'Time to Open',
  timeToReview: 'Time to Review',
  timeToFileAway: 'Time to File Away',
};

export const CONSULTATION_PROCESSING_METRIC_LABELS = {
  recordingDuration: 'Recording Duration',
  timeToReview: 'Time to File',
};

const readPath = (obj, path) => path.split('.').reduce(
  (current, key) => (current == null ? undefined : current[key]),
  obj
);

export const formatDateTimeForTooltip = (isoString) => {
  try {
    const date = new Date(isoString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  } catch (error) {
    return isoString;
  }
};

const resolveAssignee = (assigneeId, userIdToName = {}, teams = []) => {
  if (userIdToName[assigneeId]) {
    return { name: userIdToName[assigneeId], isTeam: false };
  }
  const team = teams.find((entry) => entry.teamId === assigneeId);
  if (team) {
    return { name: team.teamName, isTeam: true };
  }
  return { name: assigneeId ? assigneeId.substring(0, 5) : 'Unknown', isTeam: false };
};

export const buildAssigneeChartData = (documentsAssigned, userIdToName = {}, teams = []) => {
  const counts = {};
  (documentsAssigned || []).forEach((doc) => {
    if (!doc.assigneeId) return;
    counts[doc.assigneeId] = (counts[doc.assigneeId] || 0) + 1;
  });

  return Object.entries(counts)
    .map(([assigneeId, count]) => {
      const resolved = resolveAssignee(assigneeId, userIdToName, teams);
      return {
        assigneeId,
        assignee: resolved.name,
        count,
        isTeam: resolved.isTeam,
      };
    })
    .sort((a, b) => b.count - a.count);
};

export const parseDuration = (durationString) => {
  if (!durationString) return 0;

  try {
    const regex = /P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?)?/;
    const match = durationString.match(regex);

    if (!match) return 0;

    const years = parseInt(match[1] || 0, 10);
    const months = parseInt(match[2] || 0, 10);
    const days = parseInt(match[3] || 0, 10);
    const hours = parseInt(match[4] || 0, 10);
    const minutes = parseInt(match[5] || 0, 10);
    const seconds = parseFloat(match[6] || 0);

    return (
      years * 365.25 * 24 * 60 +
      months * 30.44 * 24 * 60 +
      days * 24 * 60 +
      hours * 60 +
      minutes +
      seconds / 60
    );
  } catch (error) {
    return 0;
  }
};

export const formatDurationMinutes = (minutes) => {
  if (minutes === 0) return '0m';

  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);

  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
};

export const resolveUserDisplayName = (userId, userIdToName = {}) => {
  if (userId === 'SYSTEM') return 'MedPrompt';
  return userIdToName[userId] || (userId ? userId.substring(0, 5) : 'Unknown');
};

const actionCountInSnapshot = (snapshot, action, filteredUserIds) => {
  if (filteredUserIds === null) {
    return snapshot.durationSnapshot?.userActionToCount?.[action] || 0;
  }
  const aggregates = snapshot.durationSnapshot?.userActionToAggregate?.[action] || [];
  return aggregates
    .filter(agg => filteredUserIds.includes(agg.userId))
    .reduce((acc, agg) => acc + agg.count, 0);
};

export const buildActivityOverTimeSeries = (
  data,
  actions = [],
  filteredUserIds = null,
  mode = AGGREGATION_CUMULATIVE
) => {
  if (!data || data.length === 0) return [];
  const cumulative = mode !== AGGREGATION_INDIVIDUAL;
  const running = {};
  return data.map(snapshot => {
    const point = { timestamp: snapshot.timestamp };
    actions.forEach(action => {
      const count = actionCountInSnapshot(snapshot, action, filteredUserIds);
      if (cumulative) {
        running[action] = (running[action] || 0) + count;
        point[action] = running[action];
      } else {
        point[action] = count;
      }
    });
    return point;
  });
};

const collectActivityUserIds = (data, selectedAction, filteredUserIds) => {
  const userIds = new Set();
  if (!data || data.length === 0) return userIds;
  data.forEach(snapshot => {
    const aggregates = snapshot.durationSnapshot?.userActionToAggregate?.[selectedAction] || [];
    aggregates.forEach(aggregate => {
      if (filteredUserIds === null || filteredUserIds.includes(aggregate.userId)) {
        userIds.add(aggregate.userId);
      }
    });
  });
  return userIds;
};

export const buildUserActivityOverTimeSeries = ({
  data,
  selectedAction,
  userIdToName = {},
  filteredUserIds = null,
  mode = AGGREGATION_CUMULATIVE,
}) => {
  const allUserIds = collectActivityUserIds(data, selectedAction, filteredUserIds);
  if (!data || data.length === 0 || allUserIds.size === 0) {
    return { userNames: [], rows: [] };
  }

  const cumulative = mode !== AGGREGATION_INDIVIDUAL;
  const running = {};
  const rows = data.map(snapshot => {
    const aggregates = snapshot.durationSnapshot?.userActionToAggregate?.[selectedAction] || [];

    if (cumulative) {
      allUserIds.forEach(userId => {
        const userName = resolveUserDisplayName(userId, userIdToName);
        if (!(userName in running)) running[userName] = 0;
      });
      aggregates.forEach(aggregate => {
        const userName = resolveUserDisplayName(aggregate.userId, userIdToName);
        running[userName] = (running[userName] || 0) + aggregate.count;
      });
      return {
        timestamp: snapshot.timestamp,
        ...running,
      };
    }

    const point = { timestamp: snapshot.timestamp };
    allUserIds.forEach(userId => {
      point[resolveUserDisplayName(userId, userIdToName)] = 0;
    });
    aggregates.forEach(aggregate => {
      const userName = resolveUserDisplayName(aggregate.userId, userIdToName);
      if (userName in point) {
        point[userName] += aggregate.count;
      }
    });
    return point;
  });

  return {
    userNames: Array.from(allUserIds).map(userId => resolveUserDisplayName(userId, userIdToName)),
    rows,
  };
};

export const buildUserActivityByStaffSeries = ({
  data,
  selectedAction,
  userIdToName = {},
  filteredUserIds = null,
}) => {
  if (!data || data.length === 0) return [];

  const userTotals = {};
  data.forEach(snapshot => {
    const aggregates = snapshot.durationSnapshot?.userActionToAggregate?.[selectedAction] || [];
    aggregates.forEach(aggregate => {
      if (filteredUserIds === null || filteredUserIds.includes(aggregate.userId)) {
        const userName = resolveUserDisplayName(aggregate.userId, userIdToName);
        userTotals[userName] = (userTotals[userName] || 0) + aggregate.count;
      }
    });
  });

  return Object.entries(userTotals)
    .map(([userName, total]) => ({ userId: userName, total }))
    .sort((a, b) => b.total - a.total);
};

export const getReadyForReviewSeries = (data, type) => {
  if (!data || data.length === 0) return [];

  const snapshotKey = data[0].storageSnapshot ? 'storageSnapshot' : 'stateSnapshot';
  const isDocuments = snapshotKey === 'storageSnapshot';
  const assignedType = isDocuments ? 'documentsAssigned' : 'tasksAssigned';

  if (type === assignedType) {
    return [
      {
        dataKey: `${snapshotKey}.${isDocuments ? 'documentsAssigned' : 'tasksAssigned'}`,
        name: 'Assigned',
        fill: '#8884d8',
      },
      {
        dataKey: `${snapshotKey}.${isDocuments ? 'documentsUnassigned' : 'tasksUnassigned'}`,
        name: 'Unassigned',
        fill: '#d0d0d0',
      },
    ];
  }

  return [
    {
      dataKey: `${snapshotKey}.${isDocuments ? 'totalDocumentsOpenedByUser' : 'totalTasksOpenedByUser'}`,
      name: 'Opened',
      fill: '#82ca9d',
    },
    {
      dataKey: `${snapshotKey}.${isDocuments ? 'documentsNeverOpened' : 'tasksNeverOpened'}`,
      name: 'Never Opened',
      fill: '#ffc658',
    },
  ];
};

export const buildReadyForReviewRows = (data, type) => {
  const series = getReadyForReviewSeries(data, type);
  if (!data || series.length === 0) return [];
  return data.map(snapshot => {
    const row = { timestamp: snapshot.timestamp };
    series.forEach(item => {
      row[item.name] = readPath(snapshot, item.dataKey) ?? 0;
    });
    return row;
  });
};

export const buildConsultationsReadyRows = (data) => {
  if (!data || data.length === 0) return [];
  return data.map(snapshot => ({
    timestamp: snapshot.timestamp,
    readyForReview: snapshot.storageSnapshot?.consultationsReadyForReview ?? 0,
  }));
};

export const buildDocumentIntakeSeries = (data) => {
  if (!data || data.length === 0) {
    return [
      { name: 'Received directly by MedPrompt', value: 0 },
      { name: 'Uploaded by users', value: 0 },
    ];
  }

  let systemCreates = 0;
  let userCreates = 0;
  data.forEach(snapshot => {
    const createAggregates = snapshot.durationSnapshot?.userActionToAggregate?.[UserAction.CREATE] || [];
    createAggregates.forEach(aggregate => {
      if (aggregate.userId === 'SYSTEM') {
        systemCreates += aggregate.count;
      } else {
        userCreates += aggregate.count;
      }
    });
  });

  return [
    { name: 'Received directly by MedPrompt', value: systemCreates },
    { name: 'Uploaded by users', value: userCreates },
  ];
};

export const buildDocumentProcessingPoints = (reviewData, selectedMetric) => {
  if (!reviewData || !reviewData.documentsReviewSummary || reviewData.documentsReviewSummary.length === 0) {
    return [];
  }

  return reviewData.documentsReviewSummary
    .map(review => {
      const durationMinutes = parseDuration(review[selectedMetric]);
      let timestampMillis = new Date(review.readyForReviewAt).getTime();

      if (selectedMetric === 'timeToReview') {
        timestampMillis += parseDuration(review.timeToOpen) * 60 * 1000;
      } else if (selectedMetric === 'timeToFileAway') {
        const timeToOpenMs = parseDuration(review.timeToOpen) * 60 * 1000;
        const timeToReviewMs = parseDuration(review.timeToReview) * 60 * 1000;
        const timeToFileAwayMs = parseDuration(review.timeToFileAway) * 60 * 1000;
        timestampMillis += timeToOpenMs + timeToReviewMs - timeToFileAwayMs;
      }

      return {
        timestamp: timestampMillis,
        duration: durationMinutes,
        documentId: review.documentId,
      };
    })
    .filter(point => point.duration > 0);
};

export const buildConsultationProcessingPoints = (reviewData, selectedMetric) => {
  if (!reviewData || !reviewData.consultationsReviewSummary || reviewData.consultationsReviewSummary.length === 0) {
    return [];
  }

  return reviewData.consultationsReviewSummary
    .map(review => ({
      timestamp: new Date(review.recordingCompletedAt).getTime(),
      duration: parseDuration(review[selectedMetric]),
    }))
    .filter(point => point.duration > 0 && Number.isFinite(point.timestamp));
};
